import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import {
  LessonsPageContainer,
  ContentWrapper,
  HeaderContainer,
  Title,
  ButtonWrapper,
  FileLinkContainer,
  BaseLinkItem,
  ExpandButton,
  ExpandIcon,
  ButtonsContainer,
} from "./LessonsPage.styles";
import Sidebar from "../../components/Sidebar";
import Table from "../../components/Table";
import Button from "../../components/Buttons/Button";
import IconButton from "../../components/Buttons/IconButton";
import { fileIcon, videoFileIcon, linkIcon } from "assets/icons";
import DeletedRow from "../../components/DeletedRow";
import RestoredRow from "../../components/RestoredRow";
import { Lesson } from "./LessonsPage.types";
import {
  fetchLessons,
  deleteLesson,
  createLesson,
  updateLesson,
  fetchLessonById,
} from "../../services/lessonService/lessonService";
import { fetchLessonCategories } from "../../services/lessonService/lessonService";
import { FileIcon } from "../../components/DragAndDropField/DragAndDropField.styles";
import LessonDetails from "../../components/LessonDetails";
import { LessonData } from "../../services/lessonService/lessonService.types";
import { ConfirmModal, CreateModal } from "../../components/Modals";
import { FileDisplay } from "../../components/FileToDisplay/FileToDisplay.types";
import LessonsButton from "../../components/Buttons/LessonsButton";
import { Tooltip } from "primereact/tooltip";
import { dropDownArrowIcon } from "assets/icons";
import { useSubscription } from '../../hooks/useSubscription';
import { useFileProgressPolling } from '../../hooks/useFileProgressPolling';
import useLessonOrdering from "../../hooks/useLessonOrdering";
import { formatServerErrors } from "../../utils/errorHandler";

import { useNotify } from "context/NotificationContext";
import { logger } from "utils/logger";
import { useListPage } from "hooks/useListPage";
import { getServerErrorData } from "utils/httpError";
import { delIcon, editIcon } from "assets/icons";
const LessonsPage: React.FC = () => {

  const {
    items: lessons,
    setItems: setLessons,
    loading,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    orderBy,
    handleSort,
    reload: loadLessons,
  } = useListPage<Lesson, Awaited<ReturnType<typeof fetchLessons>>>({
    fetcher: ({ page, pageSize: size, orderBy }) => fetchLessons(courseId, page, size, orderBy),
    select: (response) => ({ items: response.results, total: response.count }),
    errorMessage: "Ошибка загрузки уроков",
    initialPageSize: 6,
  });
  const [categories, setCategories] = useState<string[]>([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [lessonToEdit, setLessonToEdit] = useState<Lesson | null>(null);
  const [lessonToDelete, setLessonToDelete] = useState<Lesson | null>(null);
  const [pendingDelete, setPendingDelete] = useState<number | null>(null);
  const [recentlyRestored, setRecentlyRestored] = useState<number | null>(null);
  const [recentlyCreated, setRecentlyCreated] = useState<number | null>(null);
  const [recentlyEdited, setRecentlyEdited] = useState<number | null>(null);
  const [isHeaderHidden, setIsHeaderHidden] = useState<boolean>(false);
  const [isTableExpanded, setIsTableExpanded] = useState<boolean>(false);
  const [expandedRowIndex, setExpandedRowIndex] = useState<number | null>(null);
  const [externalErrors, setExternalErrors] = useState<Record<string, string>>({});
  const [isModalLoading, setIsModalLoading] = useState<boolean>(false);

  const deleteTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [storageFiles, setStorageFiles] = useState(
    [] as FileDisplay[]);

  const courseId = Number(window.location.pathname.split("/")[2]);
  const toast = useNotify();
  const { hasActiveSubscription, isLoading: isSubscriptionLoading } = useSubscription();

  const showToast = useCallback(
    (payload: { severity: "success" | "info" | "warn" | "error"; summary: string; detail: string; life?: number }) => {
      toast.show(payload);
    },
    [],
  );

  const { handleReorder, isSavingOrder } = useLessonOrdering({
    courseId,
    lessons,
    setLessons,
    showToast,
  });

  const canUseManualOrdering = !orderBy || orderBy === "order";
  const isManualOrderingDisabled =
    loading ||
    isSavingOrder ||
    lessons.length <= 1 ||
    !canUseManualOrdering ||
    expandedRowIndex !== null;

  // Автообновление прогресса файлов в модальном окне
  useFileProgressPolling({
    isActive: isCreateModalOpen,
    entityId: lessonToEdit?.id,
    filesFromStorage: storageFiles,
    updateFilesCallback: (files) => {
      // Создаем новые объекты для гарантии обновления React
      setStorageFiles(files.map(file => ({ ...file })));
    },
    fetchFunction: fetchLessonById,
    fetchParams: [courseId, lessonToEdit?.id || 0],
  });


  useEffect(() => {
    loadCategories();
  }, []);


  const handleRemoveStorageFile = (fileId: number) => {
    setStorageFiles((prevFiles) =>
      (prevFiles || []).filter((file) => file.id !== fileId)
    );
  };


  const loadCategories = async () => {
    try {
      const categories = await fetchLessonCategories(

        courseId
      );
      setCategories(categories);
    } catch (error) {
      logger.error("Ошибка загрузки категорий:", error);
      toast.show({ severity: "error", summary: "Ошибка", detail: "Ошибка загрузки категорий", life: 3000 });
    }
  };



  const handleRowExpand = (rowIndex: number | null) => {
    if (rowIndex !== null) {
      if (expandedRowIndex === null) {
        setIsHeaderHidden(true);
        setTimeout(() => {
          setIsTableExpanded(true);
        }, 700);
      }
      setExpandedRowIndex(rowIndex);
    } else {
      setIsTableExpanded(false);
      setTimeout(() => {
        setIsHeaderHidden(false);
      }, 700);
      setExpandedRowIndex(null);
    }
  };

  const getTableRows = () => {
    return lessons.map((lesson, index) => {
      const isRowExpanded = expandedRowIndex === index;
      return {
        id: lesson.id,
        order: lesson.order,
        title: lesson.title,
        category: lesson.category_title || "—",
        expandedContent: (
          <LessonDetails
            courseId={courseId}
            lessonId={lesson.id}
            title={lesson?.title || ""}
            order={lesson?.order || 1}
            content={lesson?.content || ""}
            category={lesson?.category_title || ""}
            video_url={lesson?.video_url || ""}
            files_from_storage={lesson?.files_from_storage || []}
            assignments={lesson?.assignments || []}
            onSubmit={(formData) =>
              handleCreateOrEditLesson(formData, {
                isEditing: true,
                lessonId: lesson.id,
              })
            }
            onAssignmentsUpdate={loadLessons}
            isSubscriptionActive={hasActiveSubscription}
          />
        ),
        with_assignments: lesson.with_assignments ? (
          <LessonsButton
            href={`/courses/${courseId}/lessons/${lesson.id}/assignments`}
            text="Смотреть"
          />
        ) : (
          <LessonsButton
            href={`/courses/${courseId}/lessons/${lesson.id}/assignments`}
            text="Отсутствуют"
            noLessons={true}
          />
        ),
        files: (
          <FileLinkContainer>
            {lesson.with_link && (
              <>
                <BaseLinkItem 
                  className={`file-icon-link-${index}`}
                  data-pr-tooltip="Ссылка"
                >
                  <FileIcon as={linkIcon} aria-hidden="true" />
                </BaseLinkItem>
                <Tooltip target={`.file-icon-link-${index}`} />
              </>
            )}
            {lesson.with_files && lesson.files_from_storage?.length > 0 && (
              <>
                {lesson.files_from_storage.some((file) => file.type === "video") && (
                  <>
                    <BaseLinkItem 
                      className={`file-icon-video-${index}`}
                      data-pr-tooltip="Видео"
                    >
                      <FileIcon as={videoFileIcon} aria-hidden="true" />
                    </BaseLinkItem>
                    <Tooltip target={`.file-icon-video-${index}`} />
                  </>
                )}
                {lesson.files_from_storage.some((file) => file.type !== "video") && (
                  <>
                    <BaseLinkItem 
                      className={`file-icon-file-${index}`}
                      data-pr-tooltip="Файл"
                    >
                      <FileIcon as={fileIcon} aria-hidden="true" />
                    </BaseLinkItem>
                    <Tooltip target={`.file-icon-file-${index}`} />
                  </>
                )}
              </>
            )}
          </FileLinkContainer>
        ),
        buttons: hasActiveSubscription ? (
          <ButtonsContainer>
            <IconButton
              icon={editIcon}
              altText="Редактировать"
              onClick={() => openEditModal(lesson)}
            />
            <IconButton
              icon={delIcon}
              tone="danger"
              altText="Удалить"
              onClick={() => {
                setLessonToDelete(lesson);
                setIsDeleteModalOpen(true);
              }}
            />
            <ExpandButton
              type="button"
              onClick={() => handleRowExpand(isRowExpanded ? null : index)}
              isExpanded={isRowExpanded}
              aria-expanded={isRowExpanded}
              aria-label={isRowExpanded ? "Свернуть урок" : "Развернуть урок"}
            >
              <ExpandIcon
                as={dropDownArrowIcon}
                aria-hidden="true"
                isExpanded={isRowExpanded}
              />
            </ExpandButton>
          </ButtonsContainer>
        ) : (
          <ButtonsContainer>
            <ExpandButton
              type="button"
              onClick={() => handleRowExpand(isRowExpanded ? null : index)}
              isExpanded={isRowExpanded}
              aria-expanded={isRowExpanded}
              aria-label={isRowExpanded ? "Свернуть урок" : "Развернуть урок"}
            >
              <ExpandIcon
                as={dropDownArrowIcon}
                aria-hidden="true"
                isExpanded={isRowExpanded}
              />
            </ExpandButton>
          </ButtonsContainer>
        ),
      };
    });
  };


  const openCreateModal = () => {
    setLessonToEdit(null);
    setIsCreateModalOpen(true);
  };

  const openEditModal = (lesson: Lesson) => {
    setStorageFiles(lesson?.files_from_storage || []);
    setLessonToEdit(lesson);
    setIsCreateModalOpen(true);
  };

  const handleDeleteLesson = () => {
    if (lessonToDelete) {
      const lessonId = lessonToDelete.id;
      setPendingDelete(lessonId);
      setIsDeleteModalOpen(false);
      setLessonToDelete(null);

      deleteTimeoutRef.current = setTimeout(async () => {
        try {
          await deleteLesson(courseId, lessonId);
          setPendingDelete(null);
          loadLessons();
        } catch (error) {
          logger.error("Ошибка при удалении урока:", error);
          toast.show({ severity: "error", summary: "Ошибка", detail: "Ошибка удаления урока", life: 3000 });
        }
      }, 5000);
    }
  };

  const restoreLessonHandler = (lessonId: number) => {
    if (deleteTimeoutRef.current) {
      clearTimeout(deleteTimeoutRef.current);
      deleteTimeoutRef.current = null;
    }

    setRecentlyRestored(lessonId);
    setPendingDelete(null);

    loadLessons();

    setTimeout(() => setRecentlyRestored(null), 3000);
  };

  const handleCreateOrEditLesson = async (
    formData: Record<string, string | File[] | null | FileDisplay[]>,
    options?: { isEditing?: boolean; lessonId?: number }
  ) => {
    const { isEditing, lessonId } = options || {};

    setExternalErrors({}); // Сброс ошибок
    setIsModalLoading(true); // Включаем индикатор загрузки
    
    try {
      // Получаем files_from_storage с полной информацией о типе файлов
      const filesFromStorage: FileDisplay[] = Array.isArray(formData.files_from_storage)
        ? (formData.files_from_storage as FileDisplay[])
        : Array.isArray(storageFiles)
        ? (storageFiles as FileDisplay[])
          : [];

      const uploadedFiles: File[] = Array.isArray(formData.files)
        ? (formData.files as File[])
        : [];

      const lessonData: LessonData = {
        course: courseId,
        order: Number(formData.order),
        title: formData.title as string,
        category_title: formData.category as string,
        content: (formData.content || formData.description) as string, // Поддержка обоих вариантов
        video_url: formData.video_url as string,
        files: uploadedFiles,
        files_from_storage: filesFromStorage,
      };

      if (isEditing && lessonId) {
        await updateLesson(courseId, lessonId, lessonData);
        setRecentlyEdited(lessonId);
        handleRowExpand(null);
      } else if (lessonToEdit) {
        const lessonId = lessonToEdit.id;
        await updateLesson(courseId, lessonId, lessonData);
        handleRowExpand(null);
        setRecentlyEdited(lessonId);
      } else {
        const createdLesson = await createLesson(courseId, lessonData);
        setRecentlyCreated(createdLesson.id);
      }

      await loadCategories();

      setLessonToEdit(null);
      setIsCreateModalOpen(false);
      loadLessons();

      setTimeout(() => {
        setRecentlyCreated(null);
        setRecentlyEdited(null);
      }, 3000);
    } catch (error) {
      const serverErrors = getServerErrorData(error);
      if (serverErrors) {
        const formattedErrors = formatServerErrors(serverErrors);

        setExternalErrors(formattedErrors); // Сохраняем ошибки
      } else {
        logger.error("Ошибка сохранения урока:", error);
        toast.show({
          severity: "error",
          summary: "Ошибка",
          detail: "Ошибка сохранения урока",
          life: 3000,
        });
      }
    } finally {
      setIsModalLoading(false); // Выключаем индикатор загрузки
    }
  };

  const headers = [
    { label: "№", key: "order", fieldName: "order" },
    { label: "Название", key: "title", fieldName: "title" },
    { label: "Раздел", key: "category", fieldName: "category", hidden: true },

    { label: "Материал", key: "files", fieldName: "files", hidden: true },
    {
      label: "Домашнее задание",
      key: "with_assignments",
      fieldName: "with_assignments",
      hidden: true,
    },
    { label: "", key: "buttons", sortable: false, fieldName: "" },
  ];


  const rowAmountOptions = [
    { label: "6 уроков", value: 6 },
    { label: "12 уроков", value: 12 },
    { label: "18 уроков", value: 18 },
  ];

  return (
    <LessonsPageContainer>
      <Sidebar />
      <ContentWrapper>
        <motion.div
          style={{
            overflow: "hidden",
          }}
          initial={{ height: "auto" }}
          animate={{ height: isHeaderHidden ? 0 : "auto" }}
          transition={{ duration: 0.5 }}
        >
          <HeaderContainer>
            <Title>Мои уроки</Title>
            {hasActiveSubscription && (
              <ButtonWrapper>
                <Button
                  onClick={openCreateModal}
                  variant="primary"
                  size="lg"
                >
                  Новый урок
                </Button>
              </ButtonWrapper>
            )}
          </HeaderContainer>
        </motion.div>
        <Table
          emptyState={{
            title: "Уроков пока нет",
            description:
              "Добавьте первый урок — он появится в этом списке.",
          }}
          headers={headers}
          rows={getTableRows()}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          onSort={expandedRowIndex === null ? handleSort : undefined}
          rowAmountOptions={rowAmountOptions}
          isExpanded={isTableExpanded}
          specialRowKey="title"
          loading={loading}
          expandedRowIndex={expandedRowIndex}
          isDraggable={true}
          getRowId={(row) => String(row.id)}
          onReorder={handleReorder}
          isRowDraggable={() => !isManualOrderingDisabled}
        />
        {isCreateModalOpen && (
          <CreateModal
            title={lessonToEdit ? "Редактировать урок" : "Новый урок"}
            hasDragAndDrop={true}
            fields={[
              {
                name: "order",
                type: "number",
                placeholder: "Номер урока",
                required: true,
              },
              {
                name: "title",
                type: "text",
                placeholder: "Название",
                required: true,
              },
              {
                name: "category",
                type: "selectWithInput",
                placeholder: "Категория",
                options: categories,
                required: true,
              },
              { name: "content", type: "textarea", placeholder: "Описание" },
            ]}
            onSubmit={handleCreateOrEditLesson}
            submitButtonText={lessonToEdit ? "Сохранить" : "Создать"}
            onCancel={() => setIsCreateModalOpen(false)}
            onRemoveFile={handleRemoveStorageFile}
            initialValues={
              lessonToEdit
                ? {
                  order: lessonToEdit.order.toString(),
                  title: lessonToEdit.title,
                  category: lessonToEdit.category_title || "",
                  content: lessonToEdit.content || "",
                  video_url: lessonToEdit.video_url || "",
                }
                : {}
            }
            files_from_storage={lessonToEdit ? storageFiles : []}
            externalErrors={externalErrors}
            isLoading={isModalLoading}
          />
        )}
        {isDeleteModalOpen && (
          <ConfirmModal
            isOpen={isDeleteModalOpen}
            title="Удаление урока"
            message={`Вы уверены, что хотите удалить урок "${lessonToDelete?.title}"?`}
            onConfirm={handleDeleteLesson}
            onCancel={() => setIsDeleteModalOpen(false)}
          />
        )}
      </ContentWrapper>
    </LessonsPageContainer>
  );
};

export default LessonsPage;
